// src/components/MiniRegisterModal.jsx
import React, { useEffect, useState } from "react";
import styled from "styled-components";
import axios from "axios";
import { useLanguage } from "./LanguageContext";
import { api } from "../lib/http";

const API_BASE_URL = "https://shopaholics.ge/api";

const Backdrop = styled.div`
  position: fixed; inset: 0; background: rgba(0,0,0,.4); z-index: 3001;
  display: flex; align-items: center; justify-content: center;
`;
const Box = styled.div`
  width: 420px; background: #fff; border-radius: 12px; padding: 16px 18px;
  box-shadow: 0 10px 30px rgba(0,0,0,.15);
`;
const Row = styled.div` display: flex; gap: 10px; flex-wrap: wrap; align-items: center; `;
const Input = styled.input`
  width: 100%; padding: 10px; border: 1px solid #ddd; border-radius: 8px; font-size: 14px;
  transition: border-color .2s ease;
`;
const Select = styled.select`
  width: 100%; padding: 10px; border: 1px solid #ddd; border-radius: 8px; font-size: 14px;
`;
const Actions = styled.div` display: flex; gap: 10px; margin-top: 12px; justify-content: flex-end; `;
const Btn = styled.button`
  border: 0; border-radius: 8px; padding: 8px 12px; cursor: pointer; font-weight: 700;
  background: ${p => p.secondary ? "#f3f3f3" : "rgb(20,20,20)"}; color: ${p => p.secondary ? "#333" : "#fff"};
  transition: opacity .15s ease;
  &:disabled { opacity: .6; cursor: not-allowed; }
`;

const Hint = styled.small` display:block; color:#666; `;
const ErrorText = styled.div` color:#b00020; font-size:12px; margin-top:4px; `;

const Toast = ({ language="ka", kind="error", text, onClose }) => {
  if (!text) return null;
  return (
    <div
      onClick={(e) => e.stopPropagation()}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.4)",
        zIndex: 2000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center"
      }}
    >
      <div
        style={{
          background: kind==="error" ? "#ffe8e8" : "#e8fff0",
          color: "#111",
          border: "1px solid #eee",
          boxShadow: "0 10px 24px rgba(0,0,0,.12)",
          padding: "16px 20px",
          borderRadius: 10,
          maxWidth: 320,
          textAlign: "center"
        }}
      >
        <div style={{ fontWeight: 800, marginBottom: 8 }}>
          {kind==="error" ? (language==="ka" ? "შეცდომა" : "Error") : "Info"}
        </div>
        <div>{text}</div>
        <button
          onClick={(e) => { e.stopPropagation(); onClose(); }}
          style={{
            marginTop: 12,
            background: "#111",
            color: "#fff",
            border: 0,
            borderRadius: 8,
            padding: "6px 14px",
            cursor: "pointer"
          }}
        >
          {language==="ka" ? "დახურვა" : "Close"}
        </button>
      </div>
    </div>
  );
};

// შენიშვნა: JP-ს dial-ში იყო typo `81)` — გამოვასწორე `81`-ზე
const COUNTRIES = [
  { iso: "GE", name: "Georgia (+995)", dial: "995", min: 9, max: 9 },
];

export default function MiniRegisterModal({ onClose, onSubmitted }) {
  const { language, t } = useLanguage();

  // form
  const [form, setForm] = useState({
    name: "", surname: "", email: "",
    phone: "",
    phone_country_iso: "GE",
    phone_country_code: "995",
  });

  // ui / errors
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState("");
  const [fieldErr, setFieldErr] = useState({ email: "", phone: "" });
  const [phoneInlineErr, setPhoneInlineErr] = useState("");

  // otp state
  const [otpVisible, setOtpVisible] = useState(false);
  const [otpCode, setOtpCode] = useState("");
  const [otpSending, setOtpSending] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [otpMsg, setOtpMsg] = useState("");
  const [otpTimer, setOtpTimer] = useState(0);

  // resend timer tick
  useEffect(() => {
    if (otpTimer <= 0) return;
    const id = setInterval(() => setOtpTimer(s => s - 1), 1000);
    return () => clearInterval(id);
  }, [otpTimer]);

  const onChange = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }));
  const onCountryChange = (e) => {
    const iso = e.target.value;
    const c = COUNTRIES.find(x => x.iso === iso) || COUNTRIES[0];
    setForm(f => ({ ...f, phone_country_iso: iso, phone_country_code: c.dial }));
    // reset otp on country change
    setOtpVisible(false); setOtpVerified(false); setOtpCode(""); setOtpMsg(""); setOtpTimer(0);
  };

  // helpers
  const country = COUNTRIES.find(x => x.iso === form.phone_country_iso) || COUNTRIES[0];
  const normalizeDigits = (v) => String(v || "").replace(/\D+/g, "");
  const validLen = (digits) => digits.length >= country.min && digits.length <= country.max;
  const validByCountry = (digits) => {
    if (!validLen(digits)) return false;
    if (country.iso === "GE") return digits.length === 9 && digits.startsWith("5");
    return true;
  };
  const phoneE164 = () => `+${country.dial}${normalizeDigits(form.phone)}`;

  // otp: send
  const sendOtp = async () => {
    const digits = normalizeDigits(form.phone);
    setPhoneInlineErr("");
    setOtpMsg("");

    if (!digits) {
      setPhoneInlineErr(language === "ka" ? "შეიყვანეთ ტელეფონის ნომერი." : "Enter phone number.");
      return;
    }
    if (!validByCountry(digits)) {
      setPhoneInlineErr(
        (language === "ka" ? "ტელეფონის ფორმატი არასწორია" : "Invalid phone format") +
        ` (${country.min}-${country.max})`
      );
      return;
    }

    setOtpSending(true);
    try {
      const r = await api.post(
        `${API_BASE_URL}/auth/request_sms_code.php`,
        { phone: phoneE164() },
        { headers: { "Content-Type": "application/json" }, validateStatus: () => true }
      );

      setOtpVisible(true);
      if (r.data?.ok) {
        setOtpTimer(60);
        setOtpMsg(language === "ka" ? "კოდი გამოიგზავნა" : "Code sent");
      } else {
        setOtpMsg(r.data?.msg || r.data?.debug?.response || (language === "ka" ? "ვერ გაიგზავნა" : "Failed to send"));
      }
    } catch {
      setOtpVisible(true);
      setOtpMsg(language === "ka" ? "ქსელური შეცდომა. სცადეთ ხელახლა." : "Network error. Try again.");
    } finally {
      setOtpSending(false);
    }
  };

  // otp: verify
  const verifyOtp = async () => {
    if (otpCode.length !== 6) return;
    setOtpSending(true);
    setOtpMsg("");
    try {
      const v = await api.post(
        `${API_BASE_URL}/auth/verify_sms_code.php`,
        { phone: phoneE164(), code: otpCode },
        { headers: { "Content-Type": "application/json" }, validateStatus: () => true }
      );

      if (v.data?.ok) {
        setOtpVerified(true);
        setOtpMsg(language === "ka" ? "დადასტურებულია" : "Verified");
      } else {
        setOtpVerified(false);
        setOtpMsg(v.data?.msg || (language === "ka" ? "კოდი არასწორია" : "Wrong code"));
      }
    } catch {
      setOtpVerified(false);
      setOtpMsg(language === "ka" ? "ქსელური შეცდომა. სცადეთ ხელახლა." : "Network error. Try again.");
    } finally {
      setOtpSending(false);
    }
  };

  const resendOtp = async () => {
    if (otpTimer > 0 || otpSending) return;
    await sendOtp();
  };

  // validation (for submit)
  const validateBeforeSubmit = () => {
    const errors = [];
    const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim());
    if (!emailOk) errors.push(language === "ka" ? "გთხოვთ შეიყვანოთ სწორი ელფოსტა." : "Please enter a valid email.");

    const digits = normalizeDigits(form.phone);
    if (!digits) {
      errors.push(language === "ka" ? "შეიყვანეთ ტელეფონის ნომერი." : "Enter phone number.");
    } else if (!validByCountry(digits)) {
      errors.push(
        (language === "ka" ? "ტელეფონის ფორმატი არასწორია" : "Invalid phone format") +
        ` (${country.min}-${country.max})`
      );
    }
    return { errors, digits };
  };

  const submit = async () => {
    const name = form.name.trim();
    const surname = form.surname.trim();
    const email = form.email.trim();

    if (!name || !surname || !email) {
      setToast(language === "ka" ? "ყველა ველი სავალდებულოა." : "All fields are required.");
      return;
    }

    const { errors, digits } = validateBeforeSubmit();
    if (errors.length) {
      setToast(errors[0]);
      if (errors[0].toLowerCase().includes("ელფოსტ") || errors[0].toLowerCase().includes("email")) {
        setFieldErr(f => ({ ...f, email: errors[0] }));
      } else {
        setPhoneInlineErr(errors[0]);
      }
      return;
    }

    if (!otpVerified) {
      setToast(language === "ka" ? "გთხოვთ დაადასტუროთ ტელეფონი SMS კოდით." : "Please verify your phone via SMS.");
      if (!otpVisible) setOtpVisible(true);
      return;
    }

    if (typeof onSubmitted !== "function") {
      setToast("onSubmitted handler is missing");
      return;
    }

    setLoading(true);
    setFieldErr({ email: "", phone: "" });
    setToast("");

    try {
      const data = await onSubmitted({
        name, surname, email,
        phone: digits,
        phone_country_iso: form.phone_country_iso,
        phone_country_code: form.phone_country_code,
        lang: language
      });

      const payload = data?.data || data || {};

      if (payload.success) {
        setToast(
          language === "ka"
            ? "ბმული გაიგზავნა — შეამოწმეთ ელფოსტა რეგისტრაციის დასასრულებლად."
            : "Link sent — please check your email to complete registration."
        );
        return;
      }

      // server-side field conflicts
      const code = payload.error_code || "";
      if (code === "EMAIL_EXISTS" || payload.email_taken) {
        const msg = language === "ka" ? "იმეილი უკვე არსებობს" : "Email already exists";
        setFieldErr(f => ({ ...f, email: msg }));
        setToast(msg);
      } else if (code === "PHONE_EXISTS" || payload.phone_taken) {
        const msg = language === "ka" ? "ტელეფონი უკვე არსებობს" : "Phone already exists";
        setFieldErr(f => ({ ...f, phone: msg }));
        setToast(msg);
      } else if (code === "EMAIL_AND_PHONE_EXIST") {
        setFieldErr({
          email: language === "ka" ? "იმეილი უკვე არსებობს" : "Email already exists",
          phone: language === "ka" ? "ტელეფონი უკვე არსებობს" : "Phone already exists"
        });
        setToast(language === "ka" ? "იმეილი და ტელეფონი უკვე არსებობს" : "Email and phone already exist");
      } else {
        setToast(payload.error || (language === "ka" ? "რეგისტრაცია ვერ განხორციელდა" : "Registration failed"));
      }
    } catch (e) {
      setToast(language === "ka" ? "ქსელური შეცდომა" : "Network error");
    } finally {
      setLoading(false);
    }
  };

  const c = country;

  return (
    <Backdrop
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <Toast language={language} text={toast} onClose={() => setToast("")} />

      <Box onClick={(e) => e.stopPropagation()}>
        <h3 style={{marginTop:0}}>
          {typeof t === "function" ? (t("miniregister.title") || "Quick sign up") : "Quick sign up"}
        </h3>

        {/* Name / Surname */}
        <Row>
          <Input
            name="name"
            placeholder={typeof t === "function" ? t("miniregister.firstName") : "First name"}
            onChange={onChange}
            value={form.name}
          />
          <Input
            name="surname"
            placeholder={typeof t === "function" ? t("miniregister.lastName") : "Last name"}
            onChange={onChange}
            value={form.surname}
          />
        </Row>

        {/* Email */}
        <Row style={{marginTop:8}}>
          <Input
            name="email"
            placeholder={typeof t === "function" ? t("miniregister.email") : "Email"}
            onChange={(e) => { setFieldErr(f => ({...f, email:""})); onChange(e); }}
            value={form.email}
            style={{ borderColor: fieldErr.email ? "#d33" : "#ddd" }}
          />
          {fieldErr.email ? <ErrorText>{fieldErr.email}</ErrorText> : null}
        </Row>

        {/* Phone country */}
        <Row style={{marginTop:8}}>
          <Select name="phone_country_iso" value={form.phone_country_iso} onChange={onCountryChange}>
            {COUNTRIES.map(cc => <option key={cc.iso} value={cc.iso}>{cc.name}</option>)}
          </Select>
        </Row>

        {/* Phone */}
        <Row style={{marginTop:8}}>
          <Input
            name="phone"
            placeholder={typeof t === "function" ? t("miniregister.phone") : "Phone"}
            onChange={(e) => {
              setFieldErr(f => ({...f, phone:""}));
              setPhoneInlineErr("");
              setOtpVisible(false); setOtpVerified(false); setOtpCode(""); setOtpMsg(""); setOtpTimer(0);
              onChange({ target: { name: "phone", value: e.target.value.replace(/\D+/g, "") } });
            }}
            value={form.phone}
            style={{ borderColor: (fieldErr.phone || phoneInlineErr) ? "#d33" : "#ddd" }}
          />
          {(fieldErr.phone || phoneInlineErr) ? <ErrorText>{fieldErr.phone || phoneInlineErr}</ErrorText> : null}
        </Row>

        <Hint style={{marginTop:4}}>
          {language === "ka"
            ? `მაგალითი: +${c.dial} ${"5".repeat(Math.min(Math.max(c.min, 6), 10))}`
            : `Example: +${c.dial} ${"5".repeat(Math.min(Math.max(c.min, 6), 10))}`}
        </Hint>

        {/* OTP actions */}
        {!otpVerified ? (
          <>
            <Row style={{ marginTop: 8 }}>
              <Btn onClick={sendOtp} disabled={otpSending}>
                {otpSending
                  ? (typeof t === "function" ? t("address.sending") : "Sending...")
                  : (typeof t === "function" ? t("address.verify_phone") : "Verify phone")}
              </Btn>
              <Btn
                secondary
                onClick={() => { setOtpVisible(false); setOtpMsg(""); setOtpCode(""); }}
              >
                {typeof t === "function" ? t("address.clear") : "Clear"}
              </Btn>
            </Row>

            {otpVisible && (
              <>
                <Row style={{ marginTop: 8 }}>
                  <Input
                    inputMode="numeric"
                    pattern="\d*"
                    maxLength={6}
                    placeholder={typeof t === "function" ? (t("address.code_placeholder") || "6-ნიშნა კოდი") : "6-digit code"}
                    value={otpCode}
                    onChange={(e)=>setOtpCode(e.target.value.replace(/\D/g,""))}
                  />
                </Row>
                <Row style={{ marginTop: 8 }}>
                  <Btn onClick={verifyOtp} disabled={otpSending || otpCode.length!==6}>
                    {otpSending
                      ? (typeof t === "function" ? t("address.processing") : "Processing...")
                      : (typeof t === "function" ? t("address.verify") : "Verify")}
                  </Btn>
                  <Btn secondary onClick={resendOtp} disabled={otpTimer>0 || otpSending}>
                    {(typeof t === "function" ? t("address.resend") : "Resend")}{otpTimer>0 ? ` (${otpTimer}s)` : ""}
                  </Btn>
                  <Btn secondary onClick={() => { setOtpVisible(false); setOtpCode(""); setOtpMsg(""); }}>
                    {typeof t === "function" ? t("address.cancel") : "Cancel"}
                  </Btn>
                </Row>
                {otpMsg ? <Hint style={{ marginTop: 4 }}>{otpMsg}</Hint> : null}
              </>
            )}
          </>
        ) : (
          <Row style={{ marginTop: 8 }}>
            <Hint style={{ color:"#2e7d32", fontWeight:700 }}>
              {typeof t === "function" ? (t("address.verified") || "დადასტურებულია") : "Verified"} ✔
            </Hint>
          </Row>
        )}

        {/* Submit */}
        <Actions>
          <Btn secondary onClick={onClose}>
            {typeof t === "function" ? t("miniregister.cancel") : "Cancel"}
          </Btn>
          <Btn onClick={submit} disabled={loading || !otpVerified}>
            {loading
              ? (typeof t === "function" ? t("miniregister.sending") : "Sending...")
              : (typeof t === "function" ? t("miniregister.continue") : "Continue")}
          </Btn>
        </Actions>
      </Box>
    </Backdrop>
  );
}
